import {chain} from 'lodash';
import memoize from 'lru-memoize';

import {
  AnswerFolderModel,
  AnswerOrFolderModel,
  isAnswerFolder,
} from '../../../../models/api/answerFolderModel';
import {AnswerModel} from '../../../../models/api/answerModel';

/** Generate the score for a given answer instance */
export function searchScoreForAnswerOrFolder(answerOrFolder: AnswerOrFolderModel, query: string): number {
  if (isAnswerFolder(answerOrFolder)) {
    return searchScoreForFolder(answerOrFolder, query);
  }

  return searchScoreForAnswer(answerOrFolder, query);
}

/*
 * Helpers.
 */

function searchScoreForAnswer(answer: AnswerModel, query: string): number {
  // First try to score based on `name`.
  const nameScore = searchScoreForString(answer.name, query, 10);
  if (nameScore) {
    return nameScore;
  }

  // Otherwise, score `body`.
  return searchScoreForString(answer.body, query, 1);
}

function searchScoreForFolder(folder: AnswerFolderModel, query: string): number {
  // Just try to score based on `name`.
  return searchScoreForString(folder.name, query, 10);
}

/** Generate a score for a given string */
function searchScoreForString(haystackArg: string, needleArg: string, maxScore: number): number {
  const haystack = haystackArg.toLowerCase();
  const needle = needleArg.trim().toLowerCase();
  const haystackTokens = haystack.split(' ');
  const needles = needle.split(' ');

  // Full match on a one word research ('b' will get a better score than 'aba' for a search query 'b')
  if (needles.length === 1 && haystackTokens.includes(needles[0])) {
    return maxScore;
  }

  // Full match on full query
  if (haystack.indexOf(needle) >= 0) {
    return maxScore * 0.8;
  }

  // Percentage of perfect word occurrences
  const occurances = haystackTokens.filter((token) => needles.includes(token)).length;
  const rate = occurances / haystackTokens.length;
  return (maxScore * Math.trunc((100 * rate) / 3)) / 100;
}

export const searchAnswersAndFolders = memoize(20)(<R extends AnswerOrFolderModel>(
  answersAndFolders: ReadonlyArray<R>,
  searchQuery: string,
): ReadonlyArray<R> => {
  if (!searchQuery) {
    return answersAndFolders;
  }

  return chain(answersAndFolders)
    .map((answerOrFolder) => ({
      answerOrFolder,
      score: searchScoreForAnswerOrFolder(answerOrFolder, searchQuery),
    }))
    .filter((i) => i.score > 0)
    .sortBy((i) => -i.score)
    .map((i) => i.answerOrFolder)
    .value();
});
